/*
 * Bag designer, a Shopify theme section (sections/bag-designer.liquid).
 * Reads its data from <script type="application/json" data-bgd-config> written by the section
 * and renders everything into [data-bgd-app].
 * Designs come from the "bag_design" metaobject entries; built-in designs fill in if none are set up.
 * Cart: in "event" mode each fabric is added as one continuous Measura cut (see measuraLine).
 * Holiday chips and tag search: section settings "Holiday chips" and "Holiday selected when the page opens",
 * or a link such as ?holiday=christmas.
 * Rebuilt 2026-09-28.
 */
(function () {
  'use strict';

  // ===================== shared designer core =====================
  // Same code in all three design tools (quilt, table runner, bag). Only the prefix differs.
  var FR = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'];
  var SVGNS = 'http://www.w3.org/2000/svg';
  var SEAM = 'rgba(20,20,20,.28)';
  var MONO = 'font-family="ui-monospace, Menlo, monospace"';
  var LABEL = 'fill="rgba(240,232,160,.85)" ' + MONO + ' text-anchor="middle"';
  var TAG_FAMILY = {
    white: 'light', cream: 'light', ivory: 'light', tan: 'brown', brown: 'brown', beige: 'brown',
    reds: 'red', red: 'red', pink: 'red', orange: 'orange', peaches: 'orange', peach: 'orange', rust: 'orange',
    gold: 'gold', yellow: 'gold', greens: 'green', green: 'green', blues: 'blue', blue: 'blue', teal: 'blue',
    purple: 'purple', black: 'grey', grey: 'grey', gray: 'grey', multi: 'print'
  };
  var FAMILIES = [
    ['all', 'All', null], ['light', 'Whites & creams', '#f3efe4'], ['brown', 'Tans & browns', '#9b7650'],
    ['red', 'Reds & pinks', '#c83a3a'], ['orange', 'Oranges', '#ee8a4a'], ['gold', 'Golds & yellows', '#e5c05a'],
    ['green', 'Greens', '#8fb77a'], ['blue', 'Blues', '#3d5f9a'], ['purple', 'Purples', '#8b7bc4'],
    ['grey', 'Greys & blacks', '#7d7f80'], ['print', 'Multi & prints', '#555'], ['solid', 'Solids only', null]
  ];
  var DEFAULT_HOLIDAYS = "Valentine's Day = holiday_valentines\nEaster = holiday_easter\nFourth of July = holiday_patriotic\nThanksgiving = holiday_thanksgiving\nChristmas = holiday_christmas";

  // ---------- helpers ----------
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function fmtYd(y, step) {
    step = step || 8;
    var e = Math.max(1, Math.ceil(y * step - 1e-9)) * (8 / step);
    var w = Math.floor(e / 8), f = e % 8;
    return (w ? w + (f ? ' ' : '') : '') + FR[f] + ' yd';
  }
  function inch(v) { // 1.25 → 1¼″
    var w = Math.floor(v + 1e-9), f = Math.round((v - w) * 8);
    return (w ? String(w) : '') + FR[f] + '″';
  }
  function formatMoney(cents, format) {
    format = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    function fmt(n, dec, thou, decSep) {
      var fixed = (n / 100).toFixed(dec).split('.');
      var whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return whole + (dec ? decSep + fixed[1] : '');
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2, ',', '.');
      }
    });
  }
  function area(p) {
    var a = 0;
    for (var i = 0; i < p.length; i++) {
      var q = p[i], r = p[(i + 1) % p.length];
      a += q[0] * r[1] - r[0] * q[1];
    }
    return Math.abs(a) / 2;
  }
  // Finished piece grown by the seam allowance on every side (mitered corners).
  function cutArea(p, d) {
    d = d == null ? 0.25 : d;
    var per = 0, cot = 0, n = p.length;
    for (var i = 0; i < n; i++) {
      var a = p[(i + n - 1) % n], b = p[i], c = p[(i + 1) % n];
      per += Math.hypot(c[0] - b[0], c[1] - b[1]);
      var v1 = [a[0] - b[0], a[1] - b[1]], v2 = [c[0] - b[0], c[1] - b[1]];
      var cos = (v1[0] * v2[0] + v1[1] * v2[1]) / (Math.hypot(v1[0], v1[1]) * Math.hypot(v2[0], v2[1]));
      var th = Math.acos(Math.max(-1, Math.min(1, cos)));
      cot += 1 / Math.tan(th / 2);
    }
    return area(p) + per * d + d * d * cot;
  }
  function centroid(p) {
    var x = 0, y = 0;
    p.forEach(function (q) { x += q[0]; y += q[1]; });
    return [x / p.length, y / p.length];
  }
  function pathD(p) {
    return 'M' + p.map(function (q) { return q[0] + ' ' + q[1]; }).join('L') + 'Z';
  }
  function rectP(x, y, w, h) { return [[x, y], [x + w, y], [x + w, y + h], [x, y + h]]; }
  function b64urlEncode(obj) {
    var s = btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
    return s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function b64urlDecode(s) {
    try {
      s = s.replace(/-/g, '+').replace(/_/g, '/');
      while (s.length % 4) s += '=';
      return JSON.parse(decodeURIComponent(escape(atob(s))));
    } catch (e) { return null; }
  }
  // "Andover Century Solids" → "Century Solids" when the vendor is Andover.
  function stripMaker(title, vendor) {
    title = String(title || '');
    if (vendor && title.toLowerCase().indexOf(String(vendor).toLowerCase() + ' ') === 0) {
      var rest = title.slice(String(vendor).length + 1).replace(/^[\s\-–—:·]+/, '');
      if (rest) return rest;
    }
    return title;
  }
  // For search and holiday links: lower case, no apostrophes.
  function norm(s) { return String(s || '').toLowerCase().replace(/['’‘`]/g, ''); }
  function slug(s) { return norm(s).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function parseJson(v) {
    if (v == null || v === '') return null;
    if (typeof v === 'object') return v;
    try { return JSON.parse(v); } catch (e) { return null; }
  }
  // Slots may be [{id,name,role}] or [[id,name,role]].
  function normSlots(list) {
    if (!Array.isArray(list)) return null;
    var out = list.map(function (s) {
      if (Array.isArray(s)) return { id: String(s[0]), name: s[1] || String(s[0]), role: s[2] || 'any' };
      if (s && s.id) return { id: String(s.id), name: s.name || String(s.id), role: s.role || 'any' };
      return null;
    });
    return out.every(Boolean) && out.length ? out : null;
  }
  function validPieces(list) {
    return Array.isArray(list) && list.length && list.every(function (pc) {
      return pc && pc.s && Array.isArray(pc.p) && pc.p.length >= 3;
    });
  }
  // Holiday chips: one per line, "Chip name = tag".
  function parseHolidays(text) {
    var seen = {};
    return String(text == null ? DEFAULT_HOLIDAYS : text).split(/\r?\n/).map(function (line) {
      var i = line.lastIndexOf('=');
      if (i < 1) return null;
      var name = line.slice(0, i).trim(), tag = line.slice(i + 1).trim().toLowerCase();
      if (!name || !tag || seen[tag]) return null;
      seen[tag] = true;
      return { name: name, tag: tag };
    }).filter(Boolean);
  }

  function prepFabrics(raw, cfg) {
    var perYard = cfg.cartMode === 'quarter_units' ? 4 : 1;
    return (raw || []).filter(function (f) { return f && f.available && f.variantId; }).map(function (f) {
      var tags = (f.tags || []).map(function (t) { return String(t).toLowerCase(); });
      var fam = null;
      tags.forEach(function (t) {
        if (!fam && t.indexOf('color_') === 0) fam = TAG_FAMILY[t.slice(6)] || null;
      });
      if (!fam && tags.some(function (t) { return /^theme_/.test(t); })) fam = 'print';
      var img = f.image ? (f.image.indexOf('//') === 0 ? 'https:' + f.image : f.image) : null;
      return {
        key: String(f.id),
        sku: f.sku || '',
        name: stripMaker(f.title, f.vendor),
        vendor: f.vendor || '',
        url: f.url,
        variantId: f.variantId,
        pricePerYard: (f.price || 0) * perYard, // cents
        img: img,
        tags: tags,
        sw: Math.max(1, parseFloat(f.swatchWidth) || parseFloat(cfg.defaultSwatchWidth) || 6),
        fam: fam || 'print',
        solid: /\bsolids?\b/i.test(f.title),
        tracked: !!f.tracked,
        stockYards: f.tracked ? Math.max(0, f.stock || 0) / perYard : Infinity
      };
    });
  }

  // ---------- the component ----------
  // spec = what makes this designer a quilt / runner / bag designer (see the bottom of the file).
  function Designer(root, spec) {
    var cfgEl = root.querySelector('[data-bgd-config]');
    this.spec = spec;
    this.root = root;
    this.app = root.querySelector('[data-bgd-app]');
    try { this.cfg = JSON.parse(cfgEl.textContent); } catch (e) {
      this.app.innerHTML = '<p class="bgd-loading">The ' + spec.noun + ' designer couldn’t read its settings.</p>';
      return;
    }
    this.sid = String(this.cfg.sectionId || Math.random().toString(36).slice(2)).replace(/[^\w-]/g, '');
    this.fabrics = prepFabrics(this.cfg.fabrics, this.cfg);
    this.designs = this.prepDesigns(this.cfg.designs);
    this.byKey = {};
    this.bySku = {};
    var self = this;
    this.fabrics.forEach(function (f) { self.byKey[f.key] = f; if (f.sku) self.bySku[f.sku.toUpperCase()] = f; });
    var present = {};
    this.fabrics.forEach(function (f) { f.tags.forEach(function (t) { present[t] = true; }); });
    this.holidays = parseHolidays(this.cfg.holidayChips).filter(function (h) { return present[h.tag]; });
    this.fabrics.forEach(function (f) {
      var hol = self.holidays.filter(function (h) { return f.tags.indexOf(h.tag) > -1; }).map(function (h) { return h.name; });
      f.hay = norm([f.name, f.vendor, f.sku, f.tags.join(' '), hol.join(' ')].join(' '));
    });
    if (spec.setup) spec.setup.call(this);

    if (!this.fabrics.length || !this.designs.length) {
      this.app.innerHTML = '<p class="bgd-loading">' + (!this.designs.length
        ? 'No ' + spec.noun + ' designs have been set up yet.'
        : 'No fabrics are available for the designer right now.') + '</p>';
      return;
    }
    this.patterns = {};
    this.state = this.initialState();
    this.applyUrlState();
    this.buildShell();
    this.bind();
    this.renderAll();
  }
  var P = Designer.prototype;

  // Designs from the metaobject entries; built-in ones fill any gaps (and stand in when none are set up).
  P.prepDesigns = function (raw) {
    var spec = this.spec, cfg = this.cfg, out = [], used = {};
    (raw || []).forEach(function (d) {
      if (!d) return;
      var g = parseJson(d.geometry), dflt = parseJson(d.defaults) || {};
      var builtin = spec.builtin.filter(function (b) {
        return b.id === d.id || slug(b.name) === slug(d.name) || (b.aliases || []).indexOf(d.id) > -1;
      })[0];
      var des = spec.prepGeometry(g) || (builtin && spec.prepGeometry(builtin.geometry));
      if (!des) return;
      des.id = String(d.id || slug(d.name));
      des.name = d.name || (builtin && builtin.name) || des.id;
      des.skill = d.skill || (builtin && builtin.skill) || '';
      des.sort = Number(d.sort) || 0;
      des.defaults = {};
      Object.keys((builtin && builtin.defaults) || {}).forEach(function (k) { des.defaults[k] = builtin.defaults[k]; });
      Object.keys(dflt).forEach(function (k) { des.defaults[k] = dflt[k]; });
      if (builtin) used[builtin.id] = true;
      out.push(des);
    });
    if (!out.length && cfg.builtinFallback !== false) {
      spec.builtin.forEach(function (b, i) {
        var des = spec.prepGeometry(b.geometry);
        des.id = b.id; des.name = b.name; des.skill = b.skill || ''; des.sort = i + 1; des.defaults = b.defaults || {};
        out.push(des);
      });
    }
    return out.sort(function (a, b) { return a.sort - b.sort; });
  };

  P.design = function () {
    var id = this.state.design;
    return this.designs.filter(function (d) { return d.id === id; })[0] || this.designs[0];
  };
  P.pickFabric = function (role, i) {
    var pool = this.fabrics.filter(function (f) {
      return role === 'light' ? f.fam === 'light' : role === 'dark' ? /blue|purple|grey|brown|green/.test(f.fam) : f.fam !== 'light';
    });
    if (!pool.length) pool = this.fabrics;
    return pool[i % pool.length].key;
  };
  P.skuKey = function (sku) {
    var f = sku && this.bySku[String(sku).toUpperCase()];
    return f ? f.key : null;
  };
  P.designDefaults = function (d) {
    var self = this, out = {};
    d.slots.forEach(function (s, i) { out[s.id] = self.skuKey(d.defaults[s.id]) || self.pickFabric(s.role, i * 7 + 3); });
    return out;
  };
  // Finishing parts: design default (_border etc.), then the spec's fallback SKU, then a design fabric.
  P.finishDefaults = function (d, f0) {
    var self = this, out = {}, slots = d.slots;
    var light = slots.filter(function (s) { return s.role === 'light'; })[0] || slots[0];
    var dark = slots.filter(function (s) { return s.role === 'dark'; })[0] || slots[slots.length - 1];
    var second = slots[1] || slots[0];
    var pick = { light: light, dark: dark, second: second };
    this.spec.qslots.forEach(function (q) {
      out[q.id] = self.skuKey(d.defaults['_' + q.id]) || self.skuKey(q.sku) || f0[(pick[q.from] || second).id];
    });
    return out;
  };
  P.optDefault = function (o) {
    var v = this.cfg[o.cfgKey || ('default_' + o.id)];
    return o.values.indexOf(String(v)) > -1 ? String(v) : String(o.dflt);
  };
  P.initialState = function () {
    var self = this, cfg = this.cfg, spec = this.spec;
    var first = this.designs.filter(function (d) { return d.id === cfg.defaultDesign; })[0] || this.designs[0];
    var fab = {}, opt = {};
    this.designs.forEach(function (d) { fab[d.id] = self.designDefaults(d); });
    this.options().forEach(function (o) { opt[o.id] = self.optDefault(o); });
    var views = spec.views.map(function (v) { return v[0]; });
    var hol = this.findHoliday(cfg.holidayDefault);
    return {
      design: first.id,
      view: views.indexOf(cfg.defaultView) > -1 ? cfg.defaultView : spec.defaultView,
      opt: opt,
      fab: fab,
      q: this.finishDefaults(first, fab[first.id]),
      active: 'b:' + (first.slots[1] || first.slots[0]).id,
      hover: null, fam: 'all', hol: hol ? hol.tag : '', query: ''
    };
  };
  P.options = function () {
    var self = this;
    return this.spec.options.filter(function (o) { return !o.show || o.show.call(self); }).map(function (o) {
      var opts = typeof o.opts === 'function' ? o.opts.call(self) : o.opts;
      return { id: o.id, label: o.label, opts: opts, values: opts.map(function (x) { return String(x[0]); }), dflt: o.dflt, cfgKey: o.cfgKey, group: o.group };
    });
  };
  P.opt = function (id) { return this.state.opt[id]; };
  // "christmas", "holiday_christmas", "fourth-of-july" or "Fourth of July" all find the chip.
  P.findHoliday = function (v) {
    var s = slug(v);
    if (!s) return null;
    return this.holidays.filter(function (h) {
      var t = slug(h.tag);
      return t === s || t.replace(/^holiday-/, '') === s || slug(h.name) === s;
    })[0] || null;
  };

  // ---------- shareable URL ----------
  P.urlState = function () {
    var s = this.state, d = this.design();
    var o = { d: d.id, v: s.view, o: s.opt, f: s.fab[d.id], q: s.q };
    if (s.hol) o.y = s.hol;
    return o;
  };
  P.applyUrlState = function () {
    var params, raw, s = this.state, self = this;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var h = this.findHoliday(params.get('holiday'));
    if (h) s.hol = h.tag;
    raw = params.get(this.spec.param);
    if (!raw) return;
    var o = b64urlDecode(raw);
    if (!o) return;
    var d = this.designs.filter(function (x) { return x.id === o.d; })[0];
    if (!d) return;
    s.design = d.id;
    if (this.spec.views.some(function (v) { return v[0] === o.v; })) s.view = o.v;
    if (o.o) this.options().forEach(function (op) { if (op.values.indexOf(String(o.o[op.id])) > -1) s.opt[op.id] = String(o.o[op.id]); });
    var ok = function (k) { return k && self.byKey[k]; };
    if (o.f) d.slots.forEach(function (sl) { if (ok(o.f[sl.id])) s.fab[d.id][sl.id] = o.f[sl.id]; });
    if (o.q) this.spec.qslots.forEach(function (q) { if (ok(o.q[q.id])) s.q[q.id] = o.q[q.id]; });
    if (o.y) { var hy = this.findHoliday(o.y); if (hy) s.hol = hy.tag; }
    s.active = 'b:' + (d.slots[1] || d.slots[0]).id;
  };
  P.shareUrl = function () {
    var u = new URL(window.location.href);
    u.searchParams.delete('holiday');
    u.searchParams.set(this.spec.param, b64urlEncode(this.urlState()));
    return u.toString();
  };
  P.syncUrl = function () {
    try { window.history.replaceState(window.history.state, '', this.shareUrl()); } catch (e) { /* ignore */ }
  };

  // ---------- lookups ----------
  P.keyFor = function (slotKey) {
    var s = this.state;
    if (s.hover && slotKey === s.active) return s.hover;
    return this.storedKey(slotKey);
  };
  P.storedKey = function (slotKey) {
    var parts = slotKey.split(':');
    return parts[0] === 'b' ? this.state.fab[this.design().id][parts[1]] : this.state.q[parts[1]];
  };
  P.slotEnabled = function (k) {
    var p = k.split(':');
    if (p[0] === 'b') return true;
    var q = this.spec.qslots.filter(function (x) { return x.id === p[1]; })[0];
    return !q || !q.on || q.on.call(this);
  };
  P.slotName = function (k) {
    var p = k.split(':');
    if (p[0] === 'b') return (this.design().slots.filter(function (s) { return s.id === p[1]; })[0] || {}).name;
    return (this.spec.qslots.filter(function (s) { return s.id === p[1]; })[0] || {}).name;
  };
  P.money = function (cents) { return formatMoney(Math.round(cents), this.cfg.moneyFormat); };
  P.dims = function () { return this.spec.dims.call(this); };

  // Yardage: the spec adds pieces with put(); this rounds each fabric up.
  P.yardage = function () {
    var self = this, s = this.state;
    var WOF = Number(this.cfg.usableWidth) || 40, waste = 1 + (Number(this.cfg.wastePercent) || 0) / 100;
    var raw = {}, perSlot = {}, uses = {};
    function put(slotKey, yd) {
      if (!(yd > 0)) return;
      var key = self.storedKey(slotKey);
      raw[key] = (raw[key] || 0) + yd;
      perSlot[slotKey] = (perSlot[slotKey] || 0) + yd;
      var nm = self.slotName(slotKey);
      (uses[key] = uses[key] || []).indexOf(nm) < 0 && uses[key].push(nm);
    }
    // n rectangles (cut size w × h): rows across the fabric width, whichever way round uses less.
    function rects(w, h, n) {
      function rows(a, b) { if (a > WOF) return Infinity; return Math.ceil(n / Math.floor(WOF / a)) * b; }
      var r = Math.min(rows(w, h), rows(h, w));
      if (r === Infinity) r = n * Math.ceil(w / WOF) * h; // wider than the fabric: pieced
      return r / 36;
    }
    // Strips cut across the width of fabric for a total length (border, binding, sashing).
    function strips(totalLen, cutW) { return Math.ceil(totalLen / WOF - 1e-9) * cutW / 36; }
    // Pieced parts: sum of cut areas, plus the trimming allowance.
    function pieced(slotKey, cutAreaSqIn) { put(slotKey, cutAreaSqIn * waste / (WOF * 36)); }
    this.spec.yardage.call(this, { put: put, rects: rects, strips: strips, pieced: pieced, WOF: WOF, waste: waste });
    var whole = this.cfg.cartMode === 'yards';
    var buy = {};
    Object.keys(raw).forEach(function (k) {
      buy[k] = whole ? Math.max(1, Math.ceil(raw[k] - 1e-9)) : Math.max(0.25, Math.ceil(raw[k] * 4 - 1e-9) / 4);
    });
    return { buy: buy, perSlot: perSlot, uses: uses };
  };
  P.shortages = function (buy) {
    if (!this.cfg.showStock) return [];
    var self = this;
    return Object.keys(buy).filter(function (k) { return buy[k] > self.byKey[k].stockYards; });
  };

  // ---------- SVG helpers ----------
  P.pid = function (key) { return 'bgd-' + this.sid + '-p-' + key; };
  P.ensurePattern = function (key) {
    if (this.patterns[key]) return;
    var f = this.byKey[key];
    if (!f) return;
    var W = f.sw, T = 2 * W;
    var p = document.createElementNS(SVGNS, 'pattern');
    p.setAttribute('id', this.pid(key));
    p.setAttribute('patternUnits', 'userSpaceOnUse');
    p.setAttribute('width', T);
    p.setAttribute('height', T);
    var html = '<rect width="' + T + '" height="' + T + '" fill="#c9c9c4"/>';
    if (f.img) {
      // Four mirrored copies hide the seams where the photo repeats.
      ['', 'translate(' + T + ' 0) scale(-1 1)', 'translate(0 ' + T + ') scale(1 -1)', 'translate(' + T + ' ' + T + ') scale(-1 -1)']
        .forEach(function (t) {
          html += '<image href="' + esc(f.img) + '" x="0" y="0" width="' + W + '" height="' + W + '" preserveAspectRatio="xMidYMid slice"' + (t ? ' transform="' + t + '"' : '') + '/>';
        });
    }
    p.innerHTML = html;
    this.defs.appendChild(p);
    this.patterns[key] = true;
  };
  P.fill = function (key) {
    this.ensurePattern(key);
    return 'url(#' + this.pid(key) + ')';
  };
  P.selStroke = function (sw) { return 'fill="none" stroke="var(--bgd-thread)" stroke-width="' + sw + '" stroke-dasharray=".4 .25" pointer-events="none"'; };
  // Pieces of one unit at (tx, ty); rot = quarter turns about the unit's center.
  P.piecesSVG = function (pieces, tx, ty, opts) {
    opts = opts || {};
    var self = this, o = '', sel = '', act = this.state.active;
    var tr = 'translate(' + tx + ' ' + ty + ')' + (opts.rot ? ' rotate(' + 90 * opts.rot + ' ' + opts.cx + ' ' + opts.cy + ')' : '');
    pieces.forEach(function (pc) {
      var k = 'b:' + pc.s, key = opts.fabOf ? opts.fabOf(pc.s) : self.keyFor(k);
      o += '<path d="' + pathD(pc.p) + '" fill="' + self.fill(key) + '" stroke="' + SEAM + '" stroke-width="' + (opts.seam || 0.04) + '"' + (opts.click ? ' data-slot="' + k + '"' : '') + '/>';
      if (opts.click && k === act) sel += '<path d="' + pathD(pc.p) + '" ' + self.selStroke(opts.selW || 0.1) + '/>';
    });
    return { body: '<g transform="' + tr + '">' + o + '</g>', sel: sel ? '<g transform="' + tr + '">' + sel + '</g>' : '' };
  };
  P.badge = function (x, y, n, r) {
    return '<g pointer-events="none"><circle cx="' + x + '" cy="' + y + '" r="' + r + '" fill="#fff" stroke="#17211e" stroke-width="' + r * 0.12 + '"/><text x="' + x + '" y="' + (y + r * 0.36) + '" font-size="' + r * 1.05 + '" font-weight="700" text-anchor="middle" fill="#17211e" ' + MONO + '>' + n + '</text></g>';
  };
  P.matGrid = function (x0, y0, x1, y1, step, major) {
    var g = '', x, y, w = step * 0.035, W = step * 0.06;
    for (x = Math.ceil(x0 / step) * step; x <= x1; x += step) {
      var mx = Math.abs(x % major) < 1e-6;
      g += '<line x1="' + x + '" y1="' + y0 + '" x2="' + x + '" y2="' + y1 + '" stroke="' + (mx ? 'rgba(236,226,146,.5)' : 'rgba(255,255,255,.1)') + '" stroke-width="' + (mx ? W : w) + '"/>';
    }
    for (y = Math.ceil(y0 / step) * step; y <= y1; y += step) {
      var my = Math.abs(y % major) < 1e-6;
      g += '<line x1="' + x0 + '" y1="' + y + '" x2="' + x1 + '" y2="' + y + '" stroke="' + (my ? 'rgba(236,226,146,.5)' : 'rgba(255,255,255,.1)') + '" stroke-width="' + (my ? W : w) + '"/>';
    }
    return g;
  };
  P.mat = function (X0, Y0, VW, VH, step, major) {
    return '<rect x="' + (X0 - 60) + '" y="' + (Y0 - 60) + '" width="' + (VW + 120) + '" height="' + (VH + 120) + '" fill="var(--bgd-mat)"/>' +
      this.matGrid(X0 - 60, Y0 - 60, X0 + VW + 60, Y0 + VH + 60, step, major);
  };

  // ---------- DOM ----------
  P.q = function (sel) { return this.app.querySelector(sel); };
  P.buildShell = function () {
    var id = this.sid, spec = this.spec, self = this;
    var controls = this.options().map(function (o) {
      var opts = '', groups = {};
      o.opts.forEach(function (x) {
        var html = '<option value="' + esc(x[0]) + '">' + esc(x[1]) + '</option>';
        if (x[2]) { if (!groups[x[2]]) { groups[x[2]] = []; } groups[x[2]].push(html); } else opts += html;
      });
      Object.keys(groups).forEach(function (g) { opts += '<optgroup label="' + esc(g) + '">' + groups[g].join('') + '</optgroup>'; });
      return '<label for="bgd-' + o.id + '-' + id + '">' + esc(o.label) + '<select id="bgd-' + o.id + '-' + id + '" data-bgd-opt="' + o.id + '">' + opts + '</select></label>';
    }).join('');
    this.app.innerHTML =
      '<svg class="bgd-defs" width="0" height="0" aria-hidden="true" focusable="false"><defs data-bgd-defs></defs></svg>' +
      '<div class="bgd-card bgd-stage">' +
        '<div class="bgd-blocks" data-bgd-blocks role="group" aria-label="' + esc(spec.designsLabel) + '"></div>' +
        '<div class="bgd-bar">' +
          '<div class="bgd-seg" role="group" aria-label="View">' +
            spec.views.map(function (v) { return '<button type="button" data-bgd-view="' + v[0] + '">' + esc(v[1]) + '</button>'; }).join('') +
          '</div>' +
          '<div class="bgd-muted" data-bgd-size></div>' +
        '</div>' +
        '<div class="bgd-canvas" data-bgd-canvas></div>' +
        '<div class="bgd-controls" style="--bgd-cols:' + Math.min(6, this.options().length) + '">' + controls + '</div>' +
        '<p class="bgd-hint">' + esc(spec.hint) + '</p>' +
      '</div>' +
      '<div class="bgd-layout">' +
        '<div class="bgd-card bgd-sec">' +
          '<div class="bgd-sec-h"><h3 class="bgd-h" data-bgd-slots-title></h3>' +
            '<div class="bgd-tools"><button type="button" class="bgd-ghost" data-bgd-surprise>Surprise me</button><button type="button" class="bgd-ghost" data-bgd-reset>Reset</button></div></div>' +
          '<div class="bgd-slots" data-bgd-slots></div>' +
          '<p class="bgd-sub">' + esc(spec.qTitle) + '</p>' +
          '<div class="bgd-slots" data-bgd-qslots></div>' +
          '<div class="bgd-picker">' +
            '<p class="bgd-picker-h" data-bgd-picker-h></p>' +
            '<label class="bgd-visually-hidden" for="bgd-search-' + id + '">Search fabrics</label>' +
            '<input class="bgd-search" id="bgd-search-' + id + '" type="search" placeholder="Search by name, maker, SKU or holiday" autocomplete="off" data-bgd-search>' +
            '<div class="bgd-chips" data-bgd-chips role="group" aria-label="Filter by color"></div>' +
            '<div class="bgd-hol" data-bgd-hol-row' + (this.holidays.length ? '' : ' hidden') + '><span class="bgd-hol-l">Holidays</span><div class="bgd-chips" data-bgd-hols role="group" aria-label="Filter by holiday"></div></div>' +
            '<div class="bgd-grid" data-bgd-grid></div>' +
          '</div>' +
        '</div>' +
        '<div class="bgd-card bgd-sec">' +
          '<div class="bgd-sec-h"><h3 class="bgd-h">Shopping list</h3><span class="bgd-muted" data-bgd-list-for></span></div>' +
          '<table class="bgd-list"><tbody data-bgd-list></tbody></table>' +
          '<div class="bgd-totals"><span class="bgd-muted" data-bgd-tot-yd></span><span class="bgd-big" data-bgd-tot></span></div>' +
          '<div class="bgd-warn" data-bgd-warn hidden></div>' +
          '<button type="button" class="bgd-cta" data-bgd-add>Add all to cart</button>' +
          '<div class="bgd-msg" data-bgd-msg role="status" aria-live="polite" hidden></div>' +
          '<button type="button" class="bgd-ghost bgd-share" data-bgd-share>Copy link to this design</button>' +
          '<p class="bgd-note" data-bgd-extra hidden></p>' +
          '<p class="bgd-fine" data-bgd-fine></p>' +
        '</div>' +
      '</div>';
    this.defs = this.q('[data-bgd-defs]');
    this.buildGrid();
  };
  P.buildGrid = function () {
    var self = this, present = {};
    this.fabrics.forEach(function (f) { present[f.fam] = true; if (f.solid) present.solid = true; });
    this.q('[data-bgd-chips]').innerHTML = FAMILIES.filter(function (x) { return x[0] === 'all' || present[x[0]]; }).map(function (x) {
      return '<button type="button" class="bgd-chip" data-bgd-fam="' + x[0] + '">' + (x[2] ? '<i style="background:' + x[2] + '"></i>' : '') + esc(x[1]) + '</button>';
    }).join('');
    this.q('[data-bgd-hols]').innerHTML = this.holidays.map(function (h) {
      return '<button type="button" class="bgd-chip" data-bgd-holiday="' + esc(h.tag) + '">' + esc(h.name) + '</button>';
    }).join('');
    this.q('[data-bgd-grid]').innerHTML = this.fabrics.map(function (f) {
      var low = self.cfg.showStock && f.tracked && f.stockYards < 3;
      return '<button type="button" class="bgd-fab" data-bgd-fabric="' + f.key + '" title="' + esc(f.name + (f.vendor ? ' by ' + f.vendor : '') + (f.sku ? ' · ' + f.sku : '')) + '">' +
        '<span class="bgd-sw"' + (f.img ? ' style="background-image:url(\'' + esc(f.img) + '\')"' : '') + '></span>' +
        '<b>' + esc(f.name) + '</b><small>' + self.money(f.pricePerYard) + '/yd' + (low ? ' · ' + fmtYd(f.stockYards, 4) + ' left' : '') + '</small></button>';
    }).join('') + '<p class="bgd-empty" data-bgd-nohits hidden>No fabrics match. Try another color or holiday, or clear the search.</p>';
  };
  P.swatch = function (key, cls) {
    var f = this.byKey[key];
    return '<span class="' + cls + '"' + (f && f.img ? ' style="background-image:url(\'' + esc(f.img) + '\')"' : '') + '></span>';
  };
  P.renderBlocks = function () {
    var self = this, cur = this.design().id;
    this.q('[data-bgd-blocks]').innerHTML = this.designs.map(function (d) {
      return '<button type="button" class="bgd-blk" data-bgd-design="' + esc(d.id) + '" aria-pressed="' + (d.id === cur) + '">' + self.spec.miniSVG.call(self, d) +
        '<span>' + esc(d.name) + '</span>' + (d.skill ? '<small>' + esc(d.skill) + '</small>' : '') + '</button>';
    }).join('');
  };
  P.renderStage = function () {
    this.q('[data-bgd-canvas]').innerHTML = this.spec.render.call(this, this.state.view);
  };
  P.renderSlots = function () {
    var self = this, des = this.design(), y = this.yardage(), s = this.state;
    this.q('[data-bgd-slots-title]').textContent = des.name + ' fabrics';
    this.q('[data-bgd-slots]').innerHTML = des.slots.map(function (sl, i) {
      var k = 'b:' + sl.id, f = self.byKey[s.fab[des.id][sl.id]];
      return '<button type="button" class="bgd-slot" data-bgd-slot="' + k + '" aria-pressed="' + (k === s.active) + '"><span class="bgd-num">' + (i + 1) + '</span>' + self.swatch(f.key, 'bgd-sw') +
        '<span class="bgd-t"><strong>' + esc(sl.name) + '</strong><small>' + esc(f.name) + (f.vendor ? ' · ' + esc(f.vendor) : '') + '</small></span><span class="bgd-yd">' + fmtYd(y.perSlot[k] || 0) + '</span></button>';
    }).join('');
    this.q('[data-bgd-qslots]').innerHTML = this.spec.qslots.map(function (qs) {
      var k = 'q:' + qs.id, f = self.byKey[s.q[qs.id]], en = self.slotEnabled(k);
      var sub = en ? esc(f.name) + (f.vendor ? ' · ' + esc(f.vendor) : '') : esc(qs.off || 'Not used');
      return '<button type="button" class="bgd-slot" data-bgd-slot="' + k + '" aria-pressed="' + (k === s.active) + '"' + (en ? '' : ' disabled') + '><span class="bgd-num bgd-num--q">' + esc(qs.name[0]) + '</span>' + self.swatch(f.key, 'bgd-sw') +
        '<span class="bgd-t"><strong>' + esc(qs.name) + '</strong><small>' + sub + '</small></span><span class="bgd-yd">' + (en ? fmtYd(y.perSlot[k] || 0) : '') + '</span></button>';
    }).join('');
  };
  P.fabricShown = function (f) {
    var s = this.state;
    if (s.fam !== 'all' && !(s.fam === 'solid' ? f.solid : f.fam === s.fam)) return false;
    if (s.hol && f.tags.indexOf(s.hol) < 0) return false;
    var words = norm(s.query).split(/\s+/).filter(Boolean);
    return words.every(function (w) { return f.hay.indexOf(w) > -1; });
  };
  P.renderPicker = function () {
    var s = this.state, hits = 0, self = this;
    var cur = this.storedKey(s.active);
    this.q('[data-bgd-picker-h]').innerHTML = 'Choose fabric for <b>' + esc(this.slotName(s.active)) + '</b>';
    this.app.querySelectorAll('[data-bgd-fabric]').forEach(function (el) {
      var f = self.byKey[el.getAttribute('data-bgd-fabric')];
      el.hidden = !self.fabricShown(f);
      if (!el.hidden) hits++;
      el.setAttribute('aria-pressed', String(f.key === cur));
    });
    this.q('[data-bgd-nohits]').hidden = hits > 0;
    this.app.querySelectorAll('[data-bgd-fam]').forEach(function (c) { c.setAttribute('aria-pressed', String(c.getAttribute('data-bgd-fam') === s.fam)); });
    this.app.querySelectorAll('[data-bgd-holiday]').forEach(function (c) { c.setAttribute('aria-pressed', String(c.getAttribute('data-bgd-holiday') === s.hol)); });
  };
  P.renderList = function () {
    var self = this, y = this.yardage(), buy = y.buy, spec = this.spec;
    var short = this.shortages(buy);
    var step = this.cfg.cartMode === 'yards' ? 1 : 4;
    this.q('[data-bgd-list-for]').textContent = spec.sizeText.call(this);
    this.q('[data-bgd-size]').innerHTML = spec.sizeHead.call(this);
    var keys = Object.keys(buy).sort(function (a, b) { return buy[b] - buy[a]; });
    var tot = 0, yd = 0;
    this.q('[data-bgd-list]').innerHTML = keys.map(function (k) {
      var f = self.byKey[k], cost = buy[k] * f.pricePerYard, isShort = short.indexOf(k) > -1;
      tot += cost; yd += buy[k];
      return '<tr' + (isShort ? ' class="bgd-short"' : '') + '><td class="bgd-mini-td">' + self.swatch(k, 'bgd-mini') + '</td><td><a class="bgd-nm" href="' + esc(f.url) + '">' + esc(f.name) + '</a><div class="bgd-sk">' + esc([f.vendor, f.sku].filter(Boolean).join(' · ')) + '</div>' +
        '<div class="bgd-sk">For: ' + esc(y.uses[k].join(', ')) + '</div>' +
        (isShort ? '<div class="bgd-sk bgd-sk--warn">Only ' + fmtYd(f.stockYards, 4) + ' in stock</div>' : '') +
        '</td><td class="bgd-r">' + fmtYd(buy[k], step) + '</td><td class="bgd-r">' + self.money(cost) + '</td></tr>';
    }).join('');
    this.q('[data-bgd-tot-yd]').textContent = keys.length + ' fabrics · ' + fmtYd(yd, step);
    this.q('[data-bgd-tot]').textContent = this.money(tot);
    var add = this.q('[data-bgd-add]'), warn = this.q('[data-bgd-warn]');
    if (short.length) {
      warn.hidden = false;
      warn.textContent = 'Not enough in stock: ' + short.map(function (k) { return self.byKey[k].name + ' (' + y.uses[k].join(', ') + ')'; }).join('; ') + '. Choose another fabric for those parts to continue.';
      add.disabled = true;
      add.textContent = 'Choose fabrics that are in stock';
    } else {
      warn.hidden = true;
      add.disabled = false;
      add.textContent = 'Add all ' + keys.length + ' to cart · ' + this.money(tot);
    }
    var extra = spec.extraNote ? spec.extraNote.call(this) : '';
    var ex = this.q('[data-bgd-extra]');
    ex.hidden = !extra; ex.textContent = extra;
    this.q('[data-bgd-fine]').textContent = 'Yardage includes ¼″ seams, about ' + (Number(this.cfg.wastePercent) || 0) + '% extra for trimming the pieced parts and a ' +
      (Number(this.cfg.usableWidth) || 40) + '″ usable width, rounded up to the next ' + (step === 1 ? 'yard' : '¼ yard') + '. ' + spec.fineText.call(this) +
      (step === 1 ? '' : ' Each fabric is cut in one continuous piece from the bolt.');
  };
  P.renderControls = function () {
    var s = this.state;
    this.app.querySelectorAll('[data-bgd-view]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-bgd-view') === s.view)); });
    this.app.querySelectorAll('[data-bgd-opt]').forEach(function (el) { el.value = String(s.opt[el.getAttribute('data-bgd-opt')]); });
  };
  P.renderAll = function () {
    this.renderBlocks(); this.renderStage(); this.renderSlots(); this.renderPicker(); this.renderList(); this.renderControls();
    this.syncUrl();
  };

  // ---------- actions ----------
  P.fallback = function () {
    if (!this.slotEnabled(this.state.active)) this.state.active = 'b:' + this.design().slots[0].id;
  };
  P.setActive = function (k) {
    if (!this.slotEnabled(k)) return;
    this.state.active = k; this.state.hover = null;
    this.renderStage(); this.renderSlots(); this.renderPicker();
  };
  P.setFabric = function (key) {
    var p = this.state.active.split(':');
    if (p[0] === 'b') this.state.fab[this.design().id][p[1]] = key; else this.state.q[p[1]] = key;
    this.state.hover = null;
    this.renderAll();
  };
  P.pools = function () {
    var self = this;
    function pool(fn) { var p = self.fabrics.filter(fn); return p.length ? p : self.fabrics; }
    // "Surprise me" stays inside the chosen holiday, if there are enough fabrics for it.
    var hol = this.state.hol, base = function (f) { return true; };
    if (hol && this.fabrics.filter(function (f) { return f.tags.indexOf(hol) > -1; }).length >= 3) base = function (f) { return f.tags.indexOf(hol) > -1; };
    return {
      light: pool(function (f) { return base(f) && f.fam === 'light'; }),
      dark: pool(function (f) { return base(f) && /blue|purple|grey|brown|green/.test(f.fam); }),
      mid: pool(function (f) { return base(f) && f.fam !== 'light'; })
    };
  };
  P.surprise = function () {
    var des = this.design(), used = {}, s = this.state, pools = this.pools();
    function take(p) {
      var f, t = 0;
      do { f = p[Math.floor(Math.random() * p.length)]; t++; } while (used[f.key] && t < 40);
      used[f.key] = true; return f.key;
    }
    function any(arr, dflt) { return arr.length ? arr[Math.floor(Math.random() * arr.length)] : dflt; }
    des.slots.forEach(function (sl) {
      s.fab[des.id][sl.id] = take(sl.role === 'light' ? pools.light : sl.role === 'dark' ? pools.dark : pools.mid);
    });
    var accents = des.slots.filter(function (sl) { return sl.role !== 'light'; }).map(function (sl) { return s.fab[des.id][sl.id]; });
    var lights = des.slots.filter(function (sl) { return sl.role === 'light'; }).map(function (sl) { return s.fab[des.id][sl.id]; });
    this.spec.surpriseFinish.call(this, { take: take, any: any, accents: accents, lights: lights, pools: pools });
    this.renderAll();
  };
  P.reset = function () {
    var keep = { design: this.state.design, view: this.state.view, opt: this.state.opt, hol: this.state.hol };
    this.state = this.initialState();
    this.state.design = keep.design; this.state.view = keep.view; this.state.opt = keep.opt; this.state.hol = keep.hol;
    var d = this.design();
    this.state.q = this.finishDefaults(d, this.state.fab[d.id]);
    this.state.active = 'b:' + (d.slots[1] || d.slots[0]).id;
    this.fallback();
    var search = this.q('[data-bgd-search]'); if (search) search.value = '';
    this.renderAll();
  };
  P.message = function (html, isError) {
    var m = this.q('[data-bgd-msg]');
    m.hidden = false;
    m.className = 'bgd-msg' + (isError ? ' bgd-msg--err' : '');
    m.innerHTML = html;
  };
  P.cartItems = function () {
    var self = this, y = this.yardage(), des = this.design(), mode = this.cfg.cartMode, label = this.spec.sizeText.call(this);
    return Object.keys(y.buy).map(function (k) {
      var f = self.byKey[k], yd = y.buy[k], props = {};
      props[self.spec.propLabel] = des.name + ', ' + label;
      props['Used for'] = y.uses[k].join(', ');
      return {
        id: f.variantId,
        key: k,
        quantity: mode === 'quarter_units' ? Math.round(yd * 4) : Math.ceil(yd - 1e-9),
        yards: yd,
        properties: props
      };
    });
  };
  // Measura line: one continuous piece (quantity 1) of the full length, priced per yard by Measura
  // from these properties. Format observed on the demo store, 2026-09-25. Re-test after Measura updates.
  P.measuraLine = function (item, n) {
    var f = this.byKey[item.key], yd = item.yards;
    var unitPrice = this.money(f.pricePerYard) + ' / yd';
    var props = {
      'Net length': yd + ' yd',
      'Unit price': unitPrice,
      '_measura_data': JSON.stringify({
        p: {}, pi: 0, u: unitPrice,
        m: { d: [{ v: yd, u: 'yd' }] },
        measurement: { type: 'length', dimensions: [{ unit: 'yd', value: yd }] },
        um: { v: 1, u: 'yd' }
      }),
      '_measura_item_id': String(Date.now() * 1000 + n * 97 + Math.floor(Math.random() * 97))
    };
    Object.keys(item.properties).forEach(function (k) { props[k] = item.properties[k]; });
    return { id: item.id, quantity: 1, properties: props };
  };
  P.linkList = function (items) {
    var self = this;
    return '<ul class="bgd-links">' + items.map(function (i) {
      var f = self.byKey[i.key];
      return '<li><a href="' + esc(f.url) + '" target="_blank" rel="noopener">' + esc(f.name) + (f.sku ? ' <span class="bgd-sk">' + esc(f.sku) + '</span>' : '') + '</a><b>' + fmtYd(i.yards, 4) + '</b></li>';
    }).join('') + '</ul>';
  };
  P.addToCart = function () {
    var self = this, btn = this.q('[data-bgd-add]'), ev0 = this.spec.event;
    var items = this.cartItems(), lines;
    if (this.cfg.cartMode === 'event') {
      // Other code can take over by calling preventDefault() on this event.
      var ev = new CustomEvent(ev0 + ':add', {
        bubbles: true, cancelable: true,
        detail: { items: items, design: this.design().id, size: this.spec.sizeText.call(this), shareUrl: this.shareUrl() }
      });
      this.root.dispatchEvent(ev);
      if (ev.defaultPrevented) { this.message('Adding your fabrics…'); return; }
      lines = items.map(function (i, n) { return self.measuraLine(i, n); });
    } else {
      lines = items.map(function (i) { return { id: i.id, quantity: i.quantity, properties: i.properties }; });
    }
    btn.disabled = true;
    var label = btn.textContent;
    btn.textContent = 'Adding…';
    fetch(this.cfg.cartAddUrl || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines })
    }).then(function (res) {
      return res.json().then(function (body) { return { ok: res.ok, body: body }; });
    }).then(function (r) {
      if (!r.ok) throw new Error((r.body && (r.body.description || r.body.message)) || 'The cart didn’t accept these fabrics.');
      document.dispatchEvent(new CustomEvent(ev0 + ':added', { detail: { items: items } }));
      if (self.cfg.afterAdd === 'cart_page') { window.location.href = self.cfg.cartUrl || '/cart'; return; }
      self.message('Added ' + items.length + ' fabrics to your cart. <a href="' + esc(self.cfg.cartUrl || '/cart') + '">View cart</a>');
      btn.disabled = false; btn.textContent = label;
    }).catch(function (err) {
      self.message('<p class="bgd-msg-p">' + esc(err.message) + ' Nothing was added. Please try again, or add each fabric from its own page with the amount shown:</p>' +
        self.linkList(items), true);
      btn.disabled = false; btn.textContent = label;
    });
  };
  P.copyLink = function () {
    var url = this.shareUrl(), self = this, btn = this.q('[data-bgd-share]');
    function done() { btn.textContent = 'Link copied'; setTimeout(function () { btn.textContent = 'Copy link to this design'; }, 2000); }
    function fallback() { self.message('Copy this link: <input class="bgd-search" readonly value="' + esc(url) + '" onclick="this.select()">'); }
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, fallback);
    else fallback();
  };

  P.bind = function () {
    var self = this, app = this.app;
    app.addEventListener('click', function (e) {
      var t = e.target, el;
      if ((el = t.closest('[data-bgd-design]'))) {
        self.state.design = el.getAttribute('data-bgd-design');
        var d = self.design(); self.state.active = 'b:' + (d.slots[1] || d.slots[0]).id; self.state.hover = null;
        self.renderAll(); return;
      }
      if ((el = t.closest('[data-bgd-slot]'))) {
        self.setActive(el.getAttribute('data-bgd-slot'));
        if (window.innerWidth < 990) self.q('.bgd-picker').scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if ((el = t.closest('[data-bgd-canvas] [data-slot]'))) { self.setActive(el.getAttribute('data-slot')); return; }
      if ((el = t.closest('[data-bgd-fabric]'))) { self.setFabric(el.getAttribute('data-bgd-fabric')); return; }
      if ((el = t.closest('[data-bgd-fam]'))) { self.state.fam = el.getAttribute('data-bgd-fam'); self.renderPicker(); return; }
      if ((el = t.closest('[data-bgd-holiday]'))) {
        var tag = el.getAttribute('data-bgd-holiday');
        self.state.hol = self.state.hol === tag ? '' : tag; // tap again to turn it off
        self.renderPicker(); self.syncUrl(); return;
      }
      if ((el = t.closest('[data-bgd-view]'))) { self.state.view = el.getAttribute('data-bgd-view'); self.renderAll(); return; }
      if (t.closest('[data-bgd-surprise]')) { self.surprise(); return; }
      if (t.closest('[data-bgd-reset]')) { self.reset(); return; }
      if (t.closest('[data-bgd-add]')) { self.addToCart(); return; }
      if (t.closest('[data-bgd-share]')) { self.copyLink(); return; }
    });
    app.addEventListener('change', function (e) {
      var opt = e.target.getAttribute('data-bgd-opt');
      if (!opt) return;
      self.state.opt[opt] = e.target.value;
      self.fallback();
      self.renderAll();
    });
    app.addEventListener('input', function (e) {
      if (e.target.hasAttribute('data-bgd-search')) { self.state.query = e.target.value; self.renderPicker(); }
    });
    var grid = this.q('[data-bgd-grid]');
    grid.addEventListener('mouseover', function (e) {
      var f = e.target.closest('[data-bgd-fabric]');
      if (f && self.state.hover !== f.getAttribute('data-bgd-fabric')) { self.state.hover = f.getAttribute('data-bgd-fabric'); self.renderStage(); }
    });
    grid.addEventListener('mouseleave', function () { if (self.state.hover) { self.state.hover = null; self.renderStage(); } });
  };
  // ===================== end of shared core =====================

  // ===================== bag designer =====================
  // How a bag is built (boxed-bottom tote):
  //   Each side is one flat panel Pw wide × Ph tall: optional top band, the pieced (or plain) body,
  //   then a bottom piece that wraps under the bag. A D/2 square is cut from both bottom corners and
  //   boxed, so the finished bag is (Pw − D) wide × (Ph − D/2) tall × D deep.
  // Pw = flat panel width, D = depth (gusset), body = target height of the pieced body.
  var BAGS = {
    'tote-s': { type: 'tote', name: 'Small tote', Pw: 18, D: 4, body: 12, hand: 20, shoulder: 26, strapW: 1.25 },
    'tote-m': { type: 'tote', name: 'Medium tote', Pw: 24, D: 6, body: 12, hand: 20, shoulder: 26, strapW: 1.25 },
    'tote-l': { type: 'tote', name: 'Large tote', Pw: 30, D: 6, body: 18, hand: 22, shoulder: 28, strapW: 1.25 },
    market: { type: 'market', name: 'Market bag', Pw: 24, D: 8, body: 12, hand: 22, shoulder: 28, strapW: 1.5 },
    'market-l': { type: 'market', name: 'Large market bag', Pw: 30, D: 10, body: 18, hand: 22, shoulder: 28, strapW: 1.5 }
  };
  var WRAP = 2; // the bottom fabric comes 2" up the front and back
  var BUILTIN = [{"id":"scrappy-squares","name":"Scrappy Squares","skill":"Beginner","geometry":{"unitW":3,"unitH":3,"slots":[{"id":"a","name":"Squares 1","role":"any"},{"id":"b","name":"Squares 2","role":"dark"},{"id":"c","name":"Squares 3","role":"light"},{"id":"d","name":"Squares 4","role":"any"}],"layout":[[0,1,2,3],[2,3,0,1],[1,2,3,0],[3,0,1,2]],"tiles":[{"pieces":[{"s":"a","p":[[0,0],[3,0],[3,3],[0,3]]}]},{"pieces":[{"s":"b","p":[[0,0],[3,0],[3,3],[0,3]]}]},{"pieces":[{"s":"c","p":[[0,0],[3,0],[3,3],[0,3]]}]},{"pieces":[{"s":"d","p":[[0,0],[3,0],[3,3],[0,3]]}]}]},"defaults":{"a":"RED0315","b":"BLUE5089","c":"CRM3324","d":"GOLD3269","_band":"BLUE5089","_bottom":"BLUE5089","_straps":"RED6601","_lining":"CRM3322","_pocket":"RED0315"},"aliases":["scrappy-squares-bag"]},{"id":"chevron","name":"Chevron","skill":"Beginner","geometry":{"unitW":6,"unitH":6,"slots":[{"id":"bg","name":"Background","role":"light"},{"id":"zigA","name":"Zigzag A","role":"any"},{"id":"zigB","name":"Zigzag B","role":"any"}],"layout":[[0],[1]],"tiles":[{"pieces":[{"s":"bg","p":[[0,0],[3,0],[0,3]]},{"s":"zigA","p":[[3,0],[3,3],[0,3]]},{"s":"bg","p":[[3,0],[6,0],[6,3]]},{"s":"zigA","p":[[3,0],[6,3],[3,3]]},{"s":"zigA","p":[[0,3],[3,3],[0,6]]},{"s":"bg","p":[[3,3],[3,6],[0,6]]},{"s":"zigA","p":[[3,3],[6,3],[6,6]]},{"s":"bg","p":[[3,3],[6,6],[3,6]]}]},{"pieces":[{"s":"bg","p":[[0,0],[3,0],[0,3]]},{"s":"zigB","p":[[3,0],[3,3],[0,3]]},{"s":"bg","p":[[3,0],[6,0],[6,3]]},{"s":"zigB","p":[[3,0],[6,3],[3,3]]},{"s":"zigB","p":[[0,3],[3,3],[0,6]]},{"s":"bg","p":[[3,3],[3,6],[0,6]]},{"s":"zigB","p":[[3,3],[6,3],[6,6]]},{"s":"bg","p":[[3,3],[6,6],[3,6]]}]}]},"defaults":{"bg":"CRM2953","zigA":"OR3064","zigB":"GRN2311","_band":"GRN2311","_bottom":"BR3428","_straps":"OR3064","_lining":"GOLD4603","_pocket":"OR3064"},"aliases":["chevron-bag"]},{"id":"flying-geese","name":"Flying Geese","skill":"Beginner+","geometry":{"unitW":6,"unitH":3,"slots":[{"id":"sky","name":"Sky (background)","role":"light"},{"id":"geeseA","name":"Geese A","role":"any"},{"id":"geeseB","name":"Geese B","role":"dark"}],"layout":[[0,1],[1,0]],"tiles":[{"pieces":[{"s":"geeseA","p":[[0,3],[3,0],[6,3]]},{"s":"sky","p":[[0,0],[3,0],[0,3]]},{"s":"sky","p":[[3,0],[6,0],[6,3]]}]},{"pieces":[{"s":"geeseB","p":[[0,3],[3,0],[6,3]]},{"s":"sky","p":[[0,0],[3,0],[0,3]]},{"s":"sky","p":[[3,0],[6,0],[6,3]]}]}]},"defaults":{"sky":"CRM2952","geeseA":"RED0315","geeseB":"BLUE5089","_band":"BLUE5089","_bottom":"BLUE5089","_straps":"RED6601","_lining":"BLUE6491","_pocket":"RED0315"},"aliases":["flying-geese-bag"]},{"id":"pinwheels","name":"Pinwheels","skill":"Confident beginner","geometry":{"unitW":6,"unitH":6,"slots":[{"id":"bg","name":"Background","role":"light"},{"id":"bladeA","name":"Pinwheels A","role":"any"},{"id":"bladeB","name":"Pinwheels B","role":"dark"}],"layout":[[0,1],[1,0]],"tiles":[{"pieces":[{"s":"bladeA","p":[[0,0],[3,0],[3,3]]},{"s":"bg","p":[[0,0],[3,3],[0,3]]},{"s":"bladeA","p":[[6,0],[6,3],[3,3]]},{"s":"bg","p":[[6,0],[3,3],[3,0]]},{"s":"bladeA","p":[[6,6],[3,6],[3,3]]},{"s":"bg","p":[[6,6],[3,3],[6,3]]},{"s":"bladeA","p":[[0,6],[0,3],[3,3]]},{"s":"bg","p":[[0,6],[3,3],[3,6]]}]},{"pieces":[{"s":"bladeB","p":[[0,0],[3,0],[3,3]]},{"s":"bg","p":[[0,0],[3,3],[0,3]]},{"s":"bladeB","p":[[6,0],[6,3],[3,3]]},{"s":"bg","p":[[6,0],[3,3],[3,0]]},{"s":"bladeB","p":[[6,6],[3,6],[3,3]]},{"s":"bg","p":[[6,6],[3,3],[6,3]]},{"s":"bladeB","p":[[0,6],[0,3],[3,3]]},{"s":"bg","p":[[0,6],[3,3],[3,6]]}]}]},"defaults":{"bg":"CRM7782","bladeA":"PUR5026","bladeB":"BLUE6524","_band":"PUR4850","_bottom":"PUR4850","_straps":"BLUE6524","_lining":"PUR0318","_pocket":"PUR5026"},"aliases":["pinwheel-bag"]},{"id":"basket-weave","name":"Basket Weave","skill":"Beginner","geometry":{"unitW":6,"unitH":6,"slots":[{"id":"rail1","name":"Rail 1 (outer)","role":"any"},{"id":"rail2","name":"Rail 2 (middle)","role":"light"},{"id":"rail3","name":"Rail 3 (outer)","role":"dark"}],"layout":[[0,1],[1,0]],"tiles":[{"pieces":[{"s":"rail1","p":[[0,0],[6,0],[6,2],[0,2]]},{"s":"rail2","p":[[0,2],[6,2],[6,4],[0,4]]},{"s":"rail3","p":[[0,4],[6,4],[6,6],[0,6]]}]},{"pieces":[{"s":"rail1","p":[[0,0],[2,0],[2,6],[0,6]]},{"s":"rail2","p":[[2,0],[4,0],[4,6],[2,6]]},{"s":"rail3","p":[[4,0],[6,0],[6,6],[4,6]]}]}]},"defaults":{"rail1":"TAN0301","rail2":"CRM3227","rail3":"BR3428","_band":"BR3428","_bottom":"BR3428","_straps":"TAN1934","_lining":"OR1811","_pocket":"TAN0301"},"aliases":["basket-weave-bag"]},{"id":"feature-print","name":"Feature Print","skill":"Easiest","geometry":{"unitW":3,"unitH":3,"plain":true,"slots":[{"id":"main","name":"Main print","role":"any"}],"layout":[[0]],"tiles":[{"pieces":[{"s":"main","p":[[0,0],[3,0],[3,3],[0,3]]}]}]},"defaults":{"main":"BLK8427","_band":"BLK8165","_bottom":"BLK8165","_straps":"BLK8165","_lining":"GRN2458","_pocket":"GRN2458"},"aliases":["feature-print-bag"]}];

  // Handle drawn as a cubic curve; find the rise that gives the strap its real length.
  function handleRise(span, len) {
    function arc(h) {
      var L = 0, px = 0, py = 0;
      for (var i = 1; i <= 40; i++) {
        var t = i / 40, u = 1 - t;
        var x = 3 * u * t * t * span + t * t * t * span, y = -3 * u * u * t * h - 3 * u * t * t * h;
        L += Math.hypot(x - px, y - py); px = x; py = y;
      }
      return L;
    }
    var lo = 0, hi = len;
    for (var k = 0; k < 30; k++) { var mid = (lo + hi) / 2; if (arc(mid) < len) lo = mid; else hi = mid; }
    return lo;
  }
  function prepGeometry(g) {
    g = parseJson(g);
    if (!g) return null;
    var slots = normSlots(g.slots);
    if (!slots || !Array.isArray(g.tiles) || !g.tiles.length) return null;
    var tiles = g.tiles.map(function (t) { return t && t.pieces; });
    if (!tiles.every(validPieces)) return null;
    var nT = tiles.length;
    var layout = Array.isArray(g.layout) && g.layout.length && g.layout.every(Array.isArray) ? g.layout : [[0]];
    layout = layout.map(function (row) { return row.map(function (v) { return Math.max(0, Math.min(nT - 1, Number(v) || 0)); }); });
    return { uW: Number(g.unitW) || 6, uH: Number(g.unitH) || 6, plain: !!g.plain, slots: slots, tiles: tiles, layout: layout };
  }

  P.tileAt = function (des, r, c) {
    var row = des.layout[r % des.layout.length];
    return des.tiles[row[c % row.length]];
  };
  P.bagDims = function () {
    var des = this.design(), z = BAGS[this.opt('size')] || BAGS['tote-m'];
    var nC = Math.max(1, Math.round(z.Pw / des.uW)), nR = Math.max(1, Math.round(z.body / des.uH));
    var Pw = nC * des.uW, bodyH = nR * des.uH, D = Math.min(z.D, Pw / 2), band = Number(this.opt('band')) || 0;
    var bottomH = D / 2 + WRAP, Ph = band + bodyH + bottomH;
    var W = Pw - D, H = Ph - D / 2;
    var big = W >= 16;
    return {
      z: z, nC: nC, nR: nR, Pw: Pw, Ph: Ph, bodyH: bodyH, band: band, bottomH: bottomH, D: D, W: W, H: H,
      strapLen: this.opt('handles') === 'hand' ? z.hand : z.shoulder, strapW: z.strapW,
      pocketW: big ? 10 : 8, pocketH: big ? 7 : 6,
      qty: Number(this.opt('qty')) || 1,
      lined: this.opt('lining') !== 'unlined',
      pocket: (this.opt('pocket') || 'none') === 'pocket',
      label: z.name.toLowerCase()
    };
  };

  // One flat outside panel in panel coordinates (0,0)–(Pw,Ph). badges: add numbered markers.
  P.panelSVG = function (clickable, badges) {
    var self = this, des = this.design(), d = this.dims(), s = this.state, o = '', sel = '';
    var act = s.active, dataAttr = function (k) { return clickable ? ' data-slot="' + k + '"' : ''; };
    if (d.band > 0) {
      o += '<rect x="0" y="0" width="' + d.Pw + '" height="' + d.band + '" fill="' + this.fill(this.keyFor('q:band')) + '" stroke="' + SEAM + '" stroke-width=".05"' + dataAttr('q:band') + '/>';
      if (act === 'q:band') sel += '<rect x=".1" y=".1" width="' + (d.Pw - 0.2) + '" height="' + (d.band - 0.2) + '" ' + this.selStroke(0.15) + '/>';
    }
    var y0 = d.band, best = {}, slotIdx = {};
    des.slots.forEach(function (sl, i) { slotIdx[sl.id] = i; });
    if (des.plain) {
      var k0 = 'b:' + des.slots[0].id;
      o += '<rect x="0" y="' + y0 + '" width="' + d.Pw + '" height="' + d.bodyH + '" fill="' + this.fill(this.keyFor(k0)) + '"' + dataAttr(k0) + '/>';
      if (act === k0) sel += '<rect x=".1" y="' + (y0 + 0.1) + '" width="' + (d.Pw - 0.2) + '" height="' + (d.bodyH - 0.2) + '" ' + this.selStroke(0.15) + '/>';
      best[des.slots[0].id] = { a: 1, c: [d.Pw / 2, y0 + d.bodyH / 2] };
    } else {
      for (var r = 0; r < d.nR; r++) for (var c = 0; c < d.nC; c++) {
        var tx = c * des.uW, ty = y0 + r * des.uH;
        o += '<g transform="translate(' + tx + ' ' + ty + ')">';
        this.tileAt(des, r, c).forEach(function (pc) {
          var k = 'b:' + pc.s;
          o += '<path d="' + pathD(pc.p) + '" fill="' + self.fill(self.keyFor(k)) + '" stroke="' + SEAM + '" stroke-width=".04"' + dataAttr(k) + '/>';
          if (k === act) sel += '<path transform="translate(' + tx + ' ' + ty + ')" d="' + pathD(pc.p) + '" ' + self.selStroke(0.1) + '/>';
          // spread the badges: each fabric aims for its own spot across the visible middle of the panel
          var si = slotIdx[pc.s], aimX = d.D / 2 + d.W * (si + 1) / (des.slots.length + 1);
          var a = area(pc.p), cc = centroid(pc.p), pt = [cc[0] + tx, cc[1] + ty];
          var dist = Math.hypot(pt[0] - aimX, pt[1] - (y0 + d.bodyH / 2));
          var b = best[pc.s];
          if (!b || a > b.a * 1.1 || (a > b.a * 0.9 && dist < b.dist)) best[pc.s] = { a: Math.max(a, b ? b.a : 0), c: pt, dist: dist };
        });
        o += '</g>';
      }
    }
    var by = d.band + d.bodyH;
    o += '<rect x="0" y="' + by + '" width="' + d.Pw + '" height="' + d.bottomH + '" fill="' + this.fill(this.keyFor('q:bottom')) + '" stroke="' + SEAM + '" stroke-width=".05"' + dataAttr('q:bottom') + '/>';
    if (act === 'q:bottom') sel += '<rect x=".1" y="' + (by + 0.1) + '" width="' + (d.Pw - 0.2) + '" height="' + (d.bottomH - 0.2) + '" ' + this.selStroke(0.15) + '/>';
    o += sel;
    if (badges) {
      des.slots.forEach(function (sl, k) {
        if (!best[sl.id]) return;
        o += self.badge(best[sl.id].c[0], best[sl.id].c[1], k + 1, 0.55);
      });
    }
    return o;
  };
  P.handlePath = function (x1, x2, h, dx, dy, key, clickable, dim) {
    var d = 'M' + (x1 + dx) + ' ' + (dy + 1.2) + 'L' + (x1 + dx) + ' ' + dy + 'C' + (x1 + dx) + ' ' + (dy - h) + ' ' + (x2 + dx) + ' ' + (dy - h) + ' ' + (x2 + dx) + ' ' + dy + 'L' + (x2 + dx) + ' ' + (dy + 1.2);
    var sw = this.dims().strapW, o = '';
    o += '<path d="' + d + '" fill="none" stroke="rgba(0,0,0,.35)" stroke-width="' + (sw + 0.14) + '" stroke-linecap="butt"/>';
    o += '<path d="' + d + '" fill="none" stroke="' + this.fill(this.keyFor(key)) + '" stroke-width="' + sw + '" stroke-linecap="butt"' + (clickable ? ' data-slot="' + key + '"' : '') + '/>';
    if (dim) o += '<path d="' + d + '" fill="none" stroke="rgba(0,0,0,.28)" stroke-width="' + sw + '" pointer-events="none"/>';
    if (this.state.active === key) o += '<path d="' + d + '" ' + this.selStroke(0.14) + '/>';
    return o;
  };
  // Finished bag, front and right side, looking slightly from the right.
  P.bagSVG = function () {
    var des = this.design(), d = this.dims(), s = this.state, id = this.sid;
    var W = d.W, H = d.H, D = d.D, Pw = d.Pw, h2 = D / 2, k = 0.5, sk = 0.55, dx = k * D, dy = -sk * k * D;
    var span = W * 0.5, x1 = (W - span) / 2, x2 = x1 + span, rise = handleRise(span, d.strapLen - 2);
    var top = dy - rise * 0.75, m = 4;
    var X0 = -m - 2, Y0 = top - m, X1 = W + dx + m + 2, Y1 = H + m + 1;
    var o = '<svg viewBox="' + X0 + ' ' + Y0 + ' ' + (X1 - X0) + ' ' + (Y1 - Y0) + '" role="img" aria-label="' + esc(des.name + ' ' + d.label + ', ' + W + ' by ' + H + ' by ' + D + ' inches') + '">';
    o += '<defs>' +
      '<clipPath id="bgd-' + id + '-cf"><rect x="' + h2 + '" y="0" width="' + W + '" height="' + H + '"/></clipPath>' +
      '<clipPath id="bgd-' + id + '-cr"><rect x="' + (Pw - h2) + '" y="0" width="' + h2 + '" height="' + H + '"/></clipPath>' +
      '<clipPath id="bgd-' + id + '-cb"><rect x="0" y="0" width="' + h2 + '" height="' + H + '"/></clipPath>' +
      '</defs>';
    o += this.mat(X0, Y0, X1 - X0, Y1 - Y0, 1, 6);
    o += '<path d="M-.3 ' + (H + 0.4) + 'L' + (W + 0.3) + ' ' + (H + 0.4) + 'L' + (W + dx + 0.5) + ' ' + (H + dy + 0.4) + 'L' + (W + dx + 0.5) + ' ' + (H + dy) + 'L-.3 ' + H + 'Z" fill="rgba(0,0,0,.25)"/>';
    // opening: lining (or the wrong side of the bottom fabric if unlined)
    var open = 'M0 0L' + W + ' 0L' + (W + dx) + ' ' + dy + 'L' + dx + ' ' + dy + 'Z';
    o += '<path d="' + open + '" fill="' + this.fill(this.keyFor(d.lined ? 'q:lining' : 'q:bottom')) + '"' + (d.lined ? ' data-slot="q:lining"' : '') + '/>';
    o += '<path d="' + open + '" fill="rgba(0,0,0,.4)" pointer-events="none"/>';
    if (d.lined && s.active === 'q:lining') o += '<path d="' + open + '" ' + this.selStroke(0.15) + '/>';
    o += this.handlePath(x1, x2, rise, dx, dy, 'q:straps', true, true);
    var panel = this.panelSVG(true, false);
    o += '<g transform="matrix(' + k + ' ' + (-sk * k) + ' 0 1 ' + (W - k * (Pw - h2)) + ' ' + (sk * k * (Pw - h2)) + ')"><g clip-path="url(#bgd-' + id + '-cr)">' + panel + '</g></g>';
    o += '<g transform="matrix(' + k + ' ' + (-sk * k) + ' 0 1 ' + (W + k * h2) + ' ' + (-sk * k * h2) + ')"><g clip-path="url(#bgd-' + id + '-cb)">' + panel + '</g></g>';
    o += '<path d="M' + W + ' 0L' + (W + dx) + ' ' + dy + 'L' + (W + dx) + ' ' + (H + dy) + 'L' + W + ' ' + H + 'Z" fill="rgba(0,0,0,.2)" pointer-events="none"/>';
    o += '<path d="M' + (W + dx / 2) + ' ' + (dy / 2) + 'L' + (W + dx / 2) + ' ' + (H + dy / 2) + '" stroke="rgba(0,0,0,.3)" stroke-width=".06" pointer-events="none"/>';
    o += '<g transform="translate(' + (-h2) + ' 0)"><g clip-path="url(#bgd-' + id + '-cf)">' + this.panelSVG(true, true) + '</g></g>';
    o += '<rect x="0" y="0" width="' + W + '" height="' + H + '" fill="none" stroke="rgba(0,0,0,.35)" stroke-width=".08" pointer-events="none"/>';
    o += this.handlePath(x1, x2, rise, 0, 0, 'q:straps', true, false);
    var fs = 'font-size="' + Math.max(0.9, W * 0.045) + '"';
    o += '<text x="' + W / 2 + '" y="' + (H + m * 0.62) + '" ' + LABEL + ' ' + fs + '>' + W + '″ wide</text>';
    o += '<text x="' + (-m * 0.45) + '" y="' + H / 2 + '" ' + LABEL + ' ' + fs + ' transform="rotate(-90 ' + (-m * 0.45) + ' ' + H / 2 + ')">' + H + '″ tall</text>';
    o += '<text x="' + (W + dx / 2 + 1) + '" y="' + (H + dy / 2 + m * 0.62) + '" ' + LABEL + ' ' + fs + '>' + D + '″ deep</text>';
    if (d.qty > 1) o += '<text x="' + (W + dx + m * 0.4) + '" y="' + (Y0 + m * 0.8) + '" ' + LABEL + ' ' + fs + ' text-anchor="end">×' + d.qty + '</text>';
    return o + '</svg>';
  };
  // Flat cutting layout: the outside panel (×2), lining (×2) with pocket, and the two handles.
  P.flatSVG = function () {
    var des = this.design(), d = this.dims(), s = this.state;
    var Pw = d.Pw, Ph = d.Ph, h2 = d.D / 2, gap = 3, X2 = Pw + gap, m = 3.5;
    var hasRight = d.lined || d.pocket;
    var VW = Math.max(hasRight ? 2 * Pw + gap : Pw, d.strapLen), sy = Ph + 3.2;
    var VH = sy + 2 * d.strapW + 1.6;
    var X0 = -m, Y0 = -m, fz = Math.max(0.9, Pw * 0.04), fs = 'font-size="' + fz + '"';
    var o = '<svg viewBox="' + X0 + ' ' + Y0 + ' ' + (VW + 2 * m) + ' ' + (VH + 2 * m) + '" role="img" aria-label="' + esc(des.name + ' cutting layout') + '">';
    o += this.mat(X0, Y0, VW + 2 * m, VH + 2 * m, 1, 6);
    function corners(x) {
      var c = 'fill="var(--bgd-mat)" fill-opacity=".82" stroke="var(--bgd-thread)" stroke-width=".08" stroke-dasharray=".35 .25" pointer-events="none"';
      return '<rect x="' + x + '" y="' + (Ph - h2) + '" width="' + h2 + '" height="' + h2 + '" ' + c + '/>' +
        '<rect x="' + (x + Pw - h2) + '" y="' + (Ph - h2) + '" width="' + h2 + '" height="' + h2 + '" ' + c + '/>';
    }
    o += '<rect x="-.2" y="-.2" width="' + (Pw + 0.4) + '" height="' + (Ph + 0.4) + '" fill="rgba(0,0,0,.28)"/>';
    o += this.panelSVG(true, true) + corners(0);
    o += '<text x="' + Pw / 2 + '" y="' + (-m * 0.35) + '" ' + LABEL + ' ' + fs + '>Outside, cut 2 · ' + Pw + '″ × ' + Ph + '″</text>';
    if (d.lined) {
      o += '<rect x="' + X2 + '" y="0" width="' + Pw + '" height="' + Ph + '" fill="' + this.fill(this.keyFor('q:lining')) + '" stroke="' + SEAM + '" stroke-width=".05" data-slot="q:lining"/>';
      if (s.active === 'q:lining') o += '<rect x="' + (X2 + 0.1) + '" y=".1" width="' + (Pw - 0.2) + '" height="' + (Ph - 0.2) + '" ' + this.selStroke(0.15) + '/>';
      o += corners(X2);
      o += '<text x="' + (X2 + Pw / 2) + '" y="' + (-m * 0.35) + '" ' + LABEL + ' ' + fs + '>Lining, cut 2</text>';
    }
    if (d.pocket) {
      var px = X2 + (Pw - d.pocketW) / 2, py = Math.max(2, d.band + 1.5);
      o += '<rect x="' + (px + 0.15) + '" y="' + (py + 0.2) + '" width="' + d.pocketW + '" height="' + d.pocketH + '" fill="rgba(0,0,0,.3)" pointer-events="none"/>';
      o += '<rect x="' + px + '" y="' + py + '" width="' + d.pocketW + '" height="' + d.pocketH + '" fill="' + this.fill(this.keyFor('q:pocket')) + '" stroke="rgba(20,20,20,.4)" stroke-width=".06" data-slot="q:pocket"/>';
      o += '<path d="M' + (px + d.pocketW / 2) + ' ' + (py + 0.3) + 'V' + (py + d.pocketH - 0.3) + '" stroke="rgba(20,20,20,.35)" stroke-width=".05" stroke-dasharray=".3 .2" pointer-events="none"/>';
      if (s.active === 'q:pocket') o += '<rect x="' + (px + 0.1) + '" y="' + (py + 0.1) + '" width="' + (d.pocketW - 0.2) + '" height="' + (d.pocketH - 0.2) + '" ' + this.selStroke(0.12) + '/>';
      o += '<text x="' + (px + d.pocketW / 2) + '" y="' + (py + d.pocketH + 1.2) + '" ' + LABEL + ' font-size="' + fz * 0.8 + '">Pocket ' + d.pocketW + '″ × ' + d.pocketH + '″</text>';
    }
    for (var i = 0; i < 2; i++) {
      var yy = sy + i * (d.strapW + 0.6);
      o += '<rect x="0" y="' + yy + '" width="' + d.strapLen + '" height="' + d.strapW + '" fill="' + this.fill(this.keyFor('q:straps')) + '" stroke="' + SEAM + '" stroke-width=".05" data-slot="q:straps"/>';
      if (s.active === 'q:straps') o += '<rect x=".08" y="' + (yy + 0.08) + '" width="' + (d.strapLen - 0.16) + '" height="' + (d.strapW - 0.16) + '" ' + this.selStroke(0.1) + '/>';
    }
    o += '<text x="' + (d.strapLen + 0.8) + '" y="' + (sy + d.strapW + 0.6) + '" ' + LABEL + ' font-size="' + fz * 0.85 + '" text-anchor="start">Handles ' + d.strapLen + '″ × ' + inch(d.strapW) + '</text>';
    o += '<text x="' + (-m * 0.45) + '" y="' + Ph / 2 + '" ' + LABEL + ' ' + fs + ' transform="rotate(-90 ' + (-m * 0.45) + ' ' + Ph / 2 + ')">' + Ph + '″</text>';
    return o + '</svg>';
  };

  var SPEC = {
    noun: 'bag', param: 'bag', event: 'bagdesigner', propLabel: 'Bag design',
    designsLabel: 'Bag designs', qTitle: 'Bag parts',
    hint: 'Click any part of the bag to choose its fabric. Prints are shown at approximate real size. Screen colors vary from the actual fabric.',
    views: [['bag', 'Finished bag'], ['flat', 'Cutting layout']], defaultView: 'bag',
    builtin: BUILTIN, prepGeometry: prepGeometry,
    setup: function () {
      var types = this.cfg.bagTypes || 'both';
      this.sizes = Object.keys(BAGS).filter(function (k) { return types === 'both' || BAGS[k].type === types; });
      if (!this.sizes.length) this.sizes = Object.keys(BAGS);
    },
    options: [
      { id: 'size', label: 'Bag', cfgKey: 'defaultSize', dflt: 'tote-m', opts: function () {
        var sizes = this.sizes;
        return sizes.map(function (k) { var z = BAGS[k]; return [k, z.name + ' · ' + (z.Pw - z.D) + '″', z.type === 'tote' ? 'Totes' : 'Market bags']; });
      } },
      { id: 'band', label: 'Top band', dflt: '2', opts: [['0', 'None'], ['2', '2″'], ['3', '3″']] },
      { id: 'handles', label: 'Handles', dflt: 'shoulder', opts: [['shoulder', 'Shoulder length'], ['hand', 'Hand-held']] },
      { id: 'lining', label: 'Lining', dflt: 'lined', opts: [['lined', 'Lined'], ['unlined', 'Unlined']] },
      { id: 'pocket', label: 'Pocket', dflt: 'pocket', show: function () { return this.cfg.showPocket !== false; }, opts: [['pocket', 'Inside pocket'], ['none', 'No pocket']] },
      { id: 'qty', label: 'How many', dflt: '1', opts: [['1', '1 bag'], ['2', '2 bags'], ['3', '3 bags'], ['4', '4 bags']] }
    ],
    qslots: [
      { id: 'band', name: 'Top band', from: 'dark', off: 'No top band', on: function () { return this.dims().band > 0; } },
      { id: 'bottom', name: 'Bottom', from: 'dark' },
      { id: 'straps', name: 'Handles', from: 'second' },
      { id: 'lining', name: 'Lining', from: 'light', off: 'Unlined bag', on: function () { return this.dims().lined; } },
      { id: 'pocket', name: 'Inside pocket', from: 'second', off: 'No pocket', on: function () { return this.dims().pocket; } }
    ],
    dims: function () { return this.bagDims(); },
    yardage: function (y) {
      var d = this.dims(), des = this.design(), self = this, N = d.qty, sides = 2 * N, cut = {};
      if (des.plain) {
        y.put('b:' + des.slots[0].id, y.rects(d.Pw + 0.5, d.bodyH + 0.5, sides));
      } else {
        for (var r = 0; r < d.nR; r++) for (var c = 0; c < d.nC; c++) {
          this.tileAt(des, r, c).forEach(function (pc) { cut[pc.s] = (cut[pc.s] || 0) + cutArea(pc.p); });
        }
        des.slots.forEach(function (sl) { if (cut[sl.id]) y.pieced('b:' + sl.id, sides * cut[sl.id]); });
      }
      if (d.band > 0) y.put('q:band', y.rects(d.Pw + 0.5, d.band + 0.5, sides));
      y.put('q:bottom', y.rects(d.Pw + 0.5, 2 * d.bottomH + 0.5, N)); // one piece wraps under the bag
      y.put('q:straps', y.rects(d.strapLen + 2, d.strapW * 4, 2 * N)); // folded in four, 1" tucked in at each end
      if (d.lined) y.put('q:lining', y.rects(d.Pw + 0.5, d.Ph + 0.5, sides));
      if (d.pocket) y.put('q:pocket', y.rects(d.pocketW + 0.5, 2 * d.pocketH + 0.5, N)); // folded, self-lined
      void self;
    },
    render: function (view) { return view === 'flat' ? this.flatSVG() : this.bagSVG(); },
    miniSVG: function (des) {
      var f = this.state.fab[des.id], self = this;
      var o = '<svg viewBox="0 0 12 6" aria-hidden="true" focusable="false">';
      if (des.plain) return o + '<rect width="12" height="6" fill="' + this.fill(f[des.slots[0].id]) + '"/></svg>';
      var nC = Math.ceil(12 / des.uW), nR = Math.ceil(6 / des.uH);
      for (var r = 0; r < nR; r++) for (var c = 0; c < nC; c++) {
        o += this.piecesSVG(this.tileAt(des, r, c), c * des.uW, r * des.uH, { fabOf: function (sid) { return f[sid]; } }).body;
      }
      void self;
      return o + '</svg>';
    },
    sizeText: function () {
      var d = this.dims();
      return d.W + '″ × ' + d.H + '″ × ' + d.D + '″ ' + d.label + (d.qty > 1 ? ', ×' + d.qty : '');
    },
    sizeHead: function () {
      var d = this.dims(), des = this.design();
      return '<b>' + d.W + '″ wide × ' + d.H + '″ tall × ' + d.D + '″ deep</b> · ' + d.label + (d.qty > 1 ? ' · ×' + d.qty : '') +
        (des.plain ? '' : ' · ' + d.nC + ' × ' + d.nR + ' blocks a side');
    },
    extraNote: function () {
      var d = this.dims();
      return 'You’ll also need (not in this list): fusible fleece or foam stabilizer for ' + 2 * d.qty + ' outside panels, each ' + d.Pw + '″ × ' + d.Ph + '″' +
        (d.lined ? '' : ', or a heavier fabric such as canvas') + ', and thread.';
    },
    fineText: function () {
      var d = this.dims();
      return 'The bottom is one piece that wraps under the bag and ' + WRAP + '″ up each side; ' + inch(d.D / 2) +
        ' squares are cut from the bottom corners to box them. Handles are cut ' + inch(d.strapW * 4) + ' wide and folded in four.';
    },
    surpriseFinish: function (h) {
      var s = this.state;
      s.q.bottom = h.take(h.pools.dark);
      s.q.band = Math.random() < 0.5 ? s.q.bottom : h.any(h.accents, s.q.bottom);
      s.q.straps = h.any(h.accents, s.q.bottom);
      s.q.lining = h.take(h.pools.mid);
      s.q.pocket = h.any(h.accents, s.q.lining);
    }
  };

  // ---------- boot ----------
  function init(scope) {
    (scope || document).querySelectorAll('[data-bag-designer]').forEach(function (root) {
      if (root.__bgd) return;
      root.__bgd = new Designer(root, SPEC);
    });
  }
  window.BagDesigner = Designer;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  // Theme editor: re-run when the section is added or its settings change.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
